import en from './languages/en/translations'
import sk from './languages/sk/translations'
import { getSlug } from './getSlug'
import { navigate } from 'gatsby'
import * as React from 'react'
import { element } from 'prop-types'

export const slugFormat = (title, prefix) => {
  if (prefix === 'en') {
    prefix = null
  }
  if (title && title.length > 0) {
    const slug = title
      .normalize('NFD')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/\./g, '')
      .replace(/\?/g, '')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
    if (prefix != null && prefix.length > 0) {
      return `${prefix}/${slug}`
    }
    return slug
  }
  return '#'
}

const SITE_URL = 'https://narative.sk'
const SITE_HOSTNAMES = ['narative.sk', 'www.narative.sk']
const SPECIAL_LINK_RE = /^(mailto:|tel:|javascript:|data:)/i
const ABSOLUTE_LINK_RE = /^(?:[a-z][a-z0-9+.-]*:)?\/\//i
const LEGACY_PATH_MAP = {
  '/index.php': '/',
  '/sluzby/design': '/sluzby/dizajn',
}

const normalizeSitePathname = (pathname = '/') => {
  let normalizedPath = pathname || '/'

  if (!normalizedPath.startsWith('/')) {
    normalizedPath = `/${normalizedPath}`
  }

  normalizedPath = normalizedPath.replace(/\/{2,}/g, '/')

  if (normalizedPath !== '/') {
    normalizedPath = normalizedPath.replace(/\/+$/, '')
  }

  return LEGACY_PATH_MAP[normalizedPath] || normalizedPath
}

export const isInternalSiteUrl = (rawUrl = '') => {
  if (!rawUrl) {
    return false
  }

  const trimmedUrl = String(rawUrl).trim()

  if (
    !trimmedUrl ||
    trimmedUrl.startsWith('#') ||
    trimmedUrl.startsWith('?') ||
    SPECIAL_LINK_RE.test(trimmedUrl)
  ) {
    return false
  }

  try {
    if (!ABSOLUTE_LINK_RE.test(trimmedUrl)) {
      return true
    }

    const parsedUrl = new URL(trimmedUrl, SITE_URL)
    return SITE_HOSTNAMES.includes(parsedUrl.hostname)
  } catch (error) {
    return trimmedUrl.startsWith('/')
  }
}

export const normalizeInternalUrl = (
  rawUrl = '',
  { absolute = false } = {}
) => {
  if (!rawUrl) {
    return rawUrl
  }

  const trimmedUrl = String(rawUrl).trim()

  if (
    !trimmedUrl ||
    trimmedUrl.startsWith('#') ||
    trimmedUrl.startsWith('?') ||
    SPECIAL_LINK_RE.test(trimmedUrl)
  ) {
    return trimmedUrl
  }

  try {
    const parsedUrl = new URL(trimmedUrl, SITE_URL)
    const isInternal =
      !ABSOLUTE_LINK_RE.test(trimmedUrl) ||
      SITE_HOSTNAMES.includes(parsedUrl.hostname)

    if (!isInternal) {
      return trimmedUrl
    }

    const normalizedPath = normalizeSitePathname(parsedUrl.pathname)
    const normalizedUrl = `${normalizedPath}${parsedUrl.search}${parsedUrl.hash}`

    return absolute ? `${SITE_URL}${normalizedUrl}` : normalizedUrl
  } catch (error) {
    if (!trimmedUrl.startsWith('/')) {
      return trimmedUrl
    }

    return normalizeSitePathname(trimmedUrl)
  }
}

export const normalizeInternalLinksInHtml = (html) => {
  if (!html) {
    return html
  }

  return html.replace(/href=(['"])(.*?)\1/gi, (match, quote, href) => {
    const normalizedHref = normalizeInternalUrl(href)
    return normalizedHref === href
      ? match
      : `href=${quote}${normalizedHref}${quote}`
  })
}

export const translateInternalUrlForLang = (rawUrl = '', lang = 'sk') => {
  const normalizedUrl = normalizeInternalUrl(rawUrl)

  if (!normalizedUrl || !isInternalSiteUrl(normalizedUrl)) {
    return normalizedUrl
  }

  const parsedUrl = new URL(normalizedUrl, SITE_URL)
  const normalizedPath = normalizeSitePathname(parsedUrl.pathname)
  const suffix = `${parsedUrl.search}${parsedUrl.hash}`

  if (lang === 'sk') {
    if (!normalizedPath.startsWith('/en')) {
      return `${normalizedPath}${suffix}`
    }

    const translatedPath = getTranslatedSlug(normalizedPath)
    return `${
      translatedPath === '#' ? normalizedPath : translatedPath
    }${suffix}`
  }

  if (lang === 'en') {
    if (normalizedPath.startsWith('/en')) {
      return `${normalizedPath}${suffix}`
    }

    const translatedPath = getTranslatedSlug(normalizedPath)
    return `${
      translatedPath !== '#' && translatedPath.startsWith('/en')
        ? translatedPath
        : normalizedPath
    }${suffix}`
  }

  return `${normalizedPath}${suffix}`
}

export const gs = getSlug

export const getCategoryDisplayName = (
  categoryId,
  lang = 'sk',
  transKey = 'Categories'
) => {
  const t = getTranslateFunction(lang)

  if (categoryId == null) {
    return 'Null'
  }
  categoryId = categoryId.toLowerCase()
  /*
    const categories = {
      'web-corporate': 'WebstrÃ¡nky - Corporate',
      'web-eshop': 'WebstrÃ¡nky - Eshop',
      'web-portal': 'WebstrÃ¡nky - Portal',
      'web-microsite': 'WebstrÃ¡nky - Microsite',
      'web-crm': 'WebstrÃ¡nky - CRM',
      'app-android': 'AplikÃ¡cie - Android',
      'app-ios': 'AplikÃ¡cie - iOS',
      'app-multi-platform': 'AplikÃ¡cie - Multi-platform',
      'graphic-logo-branding': 'Grafika - Logo/Branding',
      'graphic-digital/print': 'Grafika - Digital/Print',
    }
  */

  const categories = {
    corporate: t(`Misc.${transKey}.web-corporate`),
    'web-corporate': t(`Misc.${transKey}.web-corporate`),
    'web-eshop': t(`Misc.${transKey}.web-eshop`),
    'web-portal': t(`Misc.${transKey}.web-portal`),
    'web-microsite': t(`Misc.${transKey}.web-microsite`),
    'web-crm': t(`Misc.${transKey}.web-crm`),
    'app-android': t(`Misc.${transKey}.app-android`),
    'app-ios': t(`Misc.${transKey}.app-ios`),
    'app-multi-platform': t(`Misc.${transKey}.app-multi-platform`),
    'graphic-logo-branding': t(`Misc.${transKey}.graphic-logo-branding`),
    'graphic-digital/print': t(`Misc.${transKey}.graphic-digital/print`),

    corporate: t(`Misc.${transKey}.web-corporate`),
    eshop: t(`Misc.${transKey}.web-eshop`),
    portal: t(`Misc.${transKey}.web-portal`),
    microsite: t(`Misc.${transKey}.web-microsite`),
    crm: t(`Misc.${transKey}.web-crm`),
    applications: t(`Misc.${transKey}.applications`),
    android: t(`Misc.${transKey}.app-android`),
    ios: t(`Misc.${transKey}.app-ios`),
    app: t(`Misc.${transKey}.app-multi-platform`),
    multi_platform: t(`Misc.${transKey}.app-multi-platform`),
    logo_branding: t(`Misc.${transKey}.graphic-logo-branding`),
    digital_print: t(`Misc.${transKey}.graphic-digital/print`),
  }

  return categories[categoryId] ? categories[categoryId] : ''
}

export const getCategoryDisplayNameForBtn = (
  categoryId,
  lang = 'sk',
  transKey = 'Categories'
) => {
  const t = getTranslateFunction(lang)

  if (categoryId == null) {
    return 'Null'
  }
  categoryId = categoryId.toLowerCase()
  /*
    const categories = {
      'web-corporate': 'WebstrÃ¡nky - Corporate',
      'web-eshop': 'WebstrÃ¡nky - Eshop',
      'web-portal': 'WebstrÃ¡nky - Portal',
      'web-microsite': 'WebstrÃ¡nky - Microsite',
      'web-crm': 'WebstrÃ¡nky - CRM',
      'app-android': 'AplikÃ¡cie - Android',
      'app-ios': 'AplikÃ¡cie - iOS',
      'app-multi-platform': 'AplikÃ¡cie - Multi-platform',
      'graphic-logo-branding': 'Grafika - Logo/Branding',
      'graphic-digital/print': 'Grafika - Digital/Print',
    }
  */

  const categories = {
    corporate: t(`Misc.${transKey}.web-corporate`),
    'web-corporate': t(`Misc.${transKey}.web-corporate`) + ' (50+)',
    'web-eshop': t(`Misc.${transKey}.web-eshop`) + ' (40+)',
    'web-portal': t(`Misc.${transKey}.web-portal`) + ' (35+)',
    'web-microsite': t(`Misc.${transKey}.web-microsite`) + ' (15+)',
    'web-crm': t(`Misc.${transKey}.web-crm`),
    'app-android': t(`Misc.${transKey}.app-android`),
    'app-ios': t(`Misc.${transKey}.app-ios`),
    'app-multi-platform': t(`Misc.${transKey}.app-multi-platform`) + ' (20+)',
    'graphic-logo-branding':
      t(`Misc.${transKey}.graphic-logo-branding`) + ' (100+)',
    'graphic-digital/print': t(`Misc.${transKey}.graphic-digital/print`),

    corporate: t(`Misc.${transKey}.web-corporate`),
    eshop: t(`Misc.${transKey}.web-eshop`),
    portal: t(`Misc.${transKey}.web-portal`),
    microsite: t(`Misc.${transKey}.web-microsite`) + ' (15+)',
    crm: t(`Misc.${transKey}.web-crm`),
    applications: t(`Misc.${transKey}.applications`),
    android: t(`Misc.${transKey}.app-android`),
    ios: t(`Misc.${transKey}.app-ios`),
    app: t(`Misc.${transKey}.app-multi-platform`),
    multi_platform: t(`Misc.${transKey}.app-multi-platform`),
    logo_branding: t(`Misc.${transKey}.graphic-logo-branding`),
    digital_print: t(`Misc.${transKey}.graphic-digital/print`),
  }

  return categories[categoryId] ? categories[categoryId] : ''
}

export const getTranslatedSlug = (slug) => {
  const normalizedSlug = normalizeInternalUrl(slug)
  const slugPairs = {
    '/en': '/',
    '/en404': '/404',
    '/en/services': '/sluzby',
    '/en/services/service': '/sluzby/servis',
    '/en/services/design': '/sluzby/dizajn',
    '/en/services/programming': '/sluzby/programovanie',
    '/en/services/create-website': '/tvorba-webov',
    '/en/services/marketing': '/sluzby/marketing',
    '/en/services/vykon-rast': '/sluzby/vykon-rast',
    '/en/wodpress-a-woocommerce': '/wodpress-a-woocommerce',
    '/en/tvorba-web-aplikacii': '/tvorba-web-aplikacii',
    '/en/tvorba-mobilnych-aplikacii': '/tvorba-mobilnych-aplikacii',
    '/en/about-us': '/o-nas',
    '/en/about-us/ux-principes': '/o-nas/ux-principy',
    '/en/about-us/clients': '/o-nas/klienti',
    '/en/about-us/how-we-work': '/o-nas/ako-pracujeme',
    '/en/our-works': '/nase-prace',
    '/en/our-works/featured': '/nase-prace/vybrane',
    '/en/our-works/websites': '/nase-prace/webstranky',
    '/en/our-works/applications': '/nase-prace/aplikacie',
    '/en/our-works/graphics': '/nase-prace/grafika',
    '/en/blog': '/blog',
    '/en/contact': '/kontakt',
    '/en/privacy-policy': '/zasady-spracuvania-osobnych-udajov',
    '/en/brief': '/dotaznik',
    '/en/eshop-na-mieru': '/eshop-na-mieru',
    '/en/dessign': '/webdizajn',
    '/en/ux-ui-dizajn': '/ux-ui-dizajn',
    '/en/tvorba-loga': '/tvorba-loga',
    '/en/branding': '/branding',
    '/en/graficky-dizajn': '/graficky-dizajn',
    '/sluzby/vykon-rast': '/sluzby/vykon-rast',
    '/o-nas/klienti': '/o-nas/klienti',
    '/o-nas/ako-pracujeme': '/o-nas/ako-pracujeme',
    '/o-nas/ux-principy': '/o-nas/ux-principy',
  }

  if (normalizedSlug in slugPairs) {
    return normalizeInternalUrl(slugPairs[normalizedSlug])
  } else if (Object.values(slugPairs).includes(normalizedSlug)) {
    return Object.keys(slugPairs).find(
      (key) => normalizeInternalUrl(slugPairs[key]) === normalizedSlug
    )
  } else {
    return '#'
  }
}

export const matchPath = (path, find, startWith = false) => {
  const nPath = normalizeInternalUrl(path).split(/[?#]/)[0]
  const nFind = normalizeInternalUrl(find).split(/[?#]/)[0]
  const translatedFind = getTranslatedSlug(nFind)

  if (startWith) {
    return (
      nPath.startsWith(nFind) ||
      (translatedFind !== '#' && nPath.startsWith(translatedFind))
    )
  }

  return nPath === nFind || (translatedFind !== '#' && nPath === translatedFind)
}

export const getTranslateFunction = (lang_name) => {
  const lang = lang_name === 'en' ? en : sk
  return (query) => {
    let langPart = { ...lang }
    for (let q of query.split('.')) {
      if (q in langPart) {
        langPart = langPart[q]
      } else {
        console.error(
          `Error while getting translation for ${query} in ${lang_name} language!`
        )
        return 'Null'
      }
    }
    return langPart
  }
}

export const getCategorySlug = (category, lang) => {
  const en = {
    'web-corporate': 'websites/corporate',
    'web-eshop': 'websites/eshop',
    'web-portal': 'websites/portal',
    'web-microsite': 'websites/microsite',
    'web-crm': 'websites/crm',
    'app-android': 'applications',
    'app-ios': 'applications',
    'app-multi-platform': 'applications',
    applications: 'applications',
    'graphic-logo-branding': 'graphics/logo-branding',
    'graphic-digital/print': 'graphics/digital-print',

    corporate: 'websites/corporate',
    eshop: 'websites/eshop',
    portal: 'websites/portal',
    microsite: 'websites/microsite',
    crm: 'websites/crm',
    app: 'applications',
    logo_branding: 'graphics/logo-branding',
    digital_print: 'graphics/digital-print',
  }

  const sk = {
    corporate: 'webstranky/firemne',
    eshop: 'webstranky/eshopy',
    portal: 'webstranky/portaly',
    microsite: 'webstranky/microsite',
    crm: 'webstranky/crm',
    app: 'aplikacie',
    applications: 'aplikacie',
    logo_branding: 'grafika/logo-branding',
    digital_print: 'grafika/digital-print',

    corporate: 'webstranky/firemne',
    'web-eshop': 'webstranky/eshopy',
    'web-portal': 'webstranky/portaly',
    'web-microsite': 'webstranky/microsite',
    'web-crm': 'webstranky/crm',
    'app-android': 'aplikacie',
    'app-ios': 'aplikacie',
    'app-multi-platform': 'aplikacie',
    'graphic-logo-branding': 'grafika/logo-branding',
    'graphic-digital/print': 'grafika/digital-print',
  }

  return lang === 'en' ? en[category] : sk[category]
}
export const getCategorySubCategorySlug = (category, lang) => {
  const en = {
    'web-corporate': 'websites#corporate',
    'web-eshop': 'websites#eshop',
    'web-portal': 'websites#portal',
    'web-microsite': 'websites#microsite',
    'web-crm': 'websites#crm',
    'app-android': 'applications',
    'app-ios': 'applications',
    'app-multi-platform': 'applications',
    'graphic-logo-branding': 'graphics#logo-branding',
    'graphic-digital/print': 'graphics#digital-print',

    corporate: 'websites#corporate',
    eshop: 'websites#eshop',
    portal: 'websites#portal',
    microsite: 'websites#microsite',
    crm: 'websites/crm',
    app: 'applications',
    logo_branding: 'graphics#logo-branding',
    digital_print: 'graphics#digital-print',
  }

  const sk = {
    'web-corporate': 'webstranky#corporate',
    eshop: 'webstranky#eshop',
    portal: 'webstranky#portal',
    microsite: 'webstranky#microsite',
    crm: 'webstranky#crm',
    app: 'aplikacie',
    logo_branding: 'grafika#logo-branding',
    digital_print: 'grafika#digital-print',

    corporate: 'webstranky#corporate',
    'web-eshop': 'webstranky#eshop',
    'web-portal': 'webstranky#portal',
    'web-microsite': 'webstranky#microsite',
    'web-crm': 'webstranky#crm',
    'app-android': 'aplikacie',
    'app-ios': 'aplikacie',
    'app-multi-platform': 'aplikacie',
    'graphic-logo-branding': 'grafika#logo-branding',
    'graphic-digital/print': 'grafika#digital-print',
  }

  return lang === 'en' ? en[category] : sk[category]
}

export const getCategoryIWantData = (category) => {
  const sharedOfferData = {
    offer_title: 'Čo ponúkame',
    offer_text: 'Všetky funkcie',
  }

  const data = {
    'webstranky/firemne': {
      ...sharedOfferData,
      url: '/tvorba-webov',
      text: 'Chcem web',
      contact_category: 'tvorba_webu',
    },
    'webstranky/eshopy': {
      ...sharedOfferData,
      url: '/eshop-na-mieru',
      text: 'Chcem e-shop',
      cta_button_text: 'Mám záujem o E-shop',
      contact_category: 'tvorba_webu',
    },
    'webstranky/portaly': {
      ...sharedOfferData,
      url: '/tvorba-webov',
      text: 'Chcem portál',
      contact_category: 'tvorba_webu',
    },
    'webstranky/microsite': {
      ...sharedOfferData,
      url: '/tvorba-webov',
      text: 'Chcem web',
      contact_category: 'tvorba_webu',
    },
    'webstranky/crm': {
      ...sharedOfferData,
      url: '/tvorba-web-aplikacii',
      text: 'Chcem CRM',
      contact_category: 'appka',
    },
    aplikacie: {
      ...sharedOfferData,
      url: '/tvorba-web-aplikacii',
      text: 'Chcem appku',
      contact_category: 'appka',
    },
    'grafika/logo-branding': {
      ...sharedOfferData,
      url: '/tvorba-loga',
      text: 'Chcem logo',
      contact_category: 'logo_branding',
    },
    'grafika/digital-print': {
      ...sharedOfferData,
      url: '/graficky-dizajn',
      text: 'Chcem grafiku',
      contact_category: 'logo_branding',
    },
    'app-multi-platform': {
      ...sharedOfferData,
      url: '/tvorba-mobilnych-aplikacii',
      text: 'Chcem appku',
      contact_category: 'appka',
    },
  }

  return data[category]
}

export const isBrowser = () => {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

export const getWindowWidth = () => {
  if (typeof window === 'undefined' && typeof document === 'undefined') return 0
  return window.innerWidth
}

export const translatedNavigate = (url, lang) => {
  navigate(translateInternalUrlForLang(url, lang))
}

export const getRandomStr = () => {
  return Math.random().toString(36).substr(2, 9)
}

export const mapRange = (value, x1, y1, x2, y2) =>
  ((value - x1) * (y2 - x2)) / (y1 - x1) + x2

export const degrees_to_radians = (degrees) => {
  return degrees * (Math.PI / 180)
}

export const isSafari = () => {
  if (typeof window === 'undefined') return false
  return /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent)
}

const getScrollIntoViewTargetY = (el, center, offsetTop = 0) => {
  const elementRect = el.getBoundingClientRect()
  const absoluteElementTop = elementRect.top + window.pageYOffset
  const middle =
    absoluteElementTop - window.innerHeight / 2 + elementRect.height / 2
  const maxScroll = Math.max(
    0,
    document.documentElement.scrollHeight - window.innerHeight
  )

  return Math.max(
    0,
    Math.min(maxScroll, (center ? middle : absoluteElementTop) + offsetTop)
  )
}

let activeScrollAnimationFrame = null

const animateWindowScrollTo = (targetY, duration) => {
  const startY = window.pageYOffset
  const distance = targetY - startY

  if (Math.abs(distance) < 1) {
    return
  }

  if (activeScrollAnimationFrame != null) {
    window.cancelAnimationFrame(activeScrollAnimationFrame)
    activeScrollAnimationFrame = null
  }

  let startTime = null

  const easeInOutCubic = (progress) => {
    if (progress < 0.5) {
      return 4 * progress * progress * progress
    }

    return 1 - Math.pow(-2 * progress + 2, 3) / 2
  }

  const step = (currentTime) => {
    if (startTime == null) {
      startTime = currentTime
    }

    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / duration, 1)
    const easedProgress = easeInOutCubic(progress)

    window.scrollTo(0, startY + distance * easedProgress)

    if (progress < 1) {
      activeScrollAnimationFrame = window.requestAnimationFrame(step)
    } else {
      activeScrollAnimationFrame = null
    }
  }

  activeScrollAnimationFrame = window.requestAnimationFrame(step)
}

const getScrollAnimationDuration = (distance, speed) => {
  const safeSpeed = Math.max(speed, 0.1)
  const distanceAbs = Math.abs(distance)
  const baseDuration = Math.max(450, Math.min(900, distanceAbs * 0.6))
  const speedMultiplier = Math.max(1, Math.min(2, 1 / safeSpeed))

  return Math.round(baseDuration * speedMultiplier)
}

export const scrollIntoView = (
  el,
  center = true,
  inline = 'start',
  offsetTop = 0,
  speed = 1
) => {
  if (el == null || typeof window === 'undefined') return

  const targetY = getScrollIntoViewTargetY(el, center, offsetTop)

  if (speed !== 1) {
    animateWindowScrollTo(
      targetY,
      getScrollAnimationDuration(targetY - window.pageYOffset, speed)
    )
    return
  }

  if (!isSafari()) {
    if (offsetTop !== 0) {
      window.scrollTo({
        top: targetY,
        behavior: 'smooth',
      })
    } else {
      el.scrollIntoView({
        behavior: 'smooth',
        block: center ? 'center' : 'start',
        inline: inline === null ? undefined : inline,
      })
    }
  } else {
    window.scrollTo(0, targetY)
  }
}

export const scrollToTarget = function (target, duration = 800) {
  const top = target.getBoundingClientRect().top
  const startPos = window.pageYOffset
  const diff = top

  let startTime = null
  let requestId

  const loop = function (currentTime) {
    if (!startTime) {
      startTime = currentTime
    }

    // Elapsed time in miliseconds
    const time = currentTime - startTime

    const percent = Math.min(time / duration, 1)
    window.scrollTo(0, startPos + diff * percent)

    if (time < duration) {
      // Continue moving
      requestId = window.requestAnimationFrame(loop)
    } else {
      window.cancelAnimationFrame(requestId)
    }
  }
  requestId = window.requestAnimationFrame(loop)
}

export const uppercaseFirstLetter = function (string) {
  return string.substring(0, 1).toUpperCase() + string.substring(1)
}
export class AnchorClickToNavigateWrapper extends React.Component {
  constructor(props) {
    super(props)
    this.ref = React.createRef()
    this.updateLinksHandlers = this.updateLinksHandlers.bind(this)
  }
  updateLinksHandlers() {
    setTimeout(() => {
      if (this.ref && this.ref.current) {
        this.ref.current.querySelectorAll('a').forEach((el) => {
          const href = el.getAttribute('href')
          const normalizedHref = normalizeInternalUrl(href)

          if (normalizedHref && normalizedHref !== href) {
            el.setAttribute('href', normalizedHref)
          }

          if (
            !isInternalSiteUrl(href) ||
            el.getAttribute('target') === '_blank'
          ) {
            el.onclick = null
            return
          }

          el.onclick = (event) => {
            event.preventDefault()
            navigate(normalizedHref)
          }
        })
      }
    }, 0)
  }

  componentDidMount() {
    this.updateLinksHandlers()
  }

  render() {
    const { children } = this.props
    this.updateLinksHandlers()
    return <div ref={this.ref}>{children}</div>
  }
}

export const shiftHTags = (html) => {
  if (!html) return html
  return html.replace(/<h6>/g, '<p class="blog-small-text">')

  return html
    .replace(/<h2>/g, '<h3>')
    .replace(/<\/h2>/g, '</h3>')
    .replace(/<h1>/g, '<h2>')
    .replace(/<\/h1>/g, '</h2>')
}

export const toLineBreak = (html) => {
  if (!html) return html
  return html.replaceAll('$$', '<br>')
}

export const removeNumberPrefix = (str) => {
  return replaceUnderscore(str.replace(/^[0-9][0-9]/gm, ''))
}
export const replaceUnderscore = (str) => {
  return str.replaceAll('_', ' ')
}

export const convertNonBreakingSpace = (str) => {
  return str.replace('&nbsp', ' ')
}

export const scrollToSlug = (plussY = 0) => {
  let id = window.location.hash.replace('#', '')
  setTimeout(() => {
    let element = document.getElementById(id)
    if (element) {
      window.scrollTo(1, element.offsetTop + plussY)
    }
  }, 500)
  /* DEBUGED */
  /*  let maxTry = 5; */
  /* const interval = setInterval(() => {
      let element = document.getElementById(id);
         
      if (element) {
        if(element.getBoundingClientRect().top < -50 || element.getBoundingClientRect().top > 50) {
          console.log('scrolled', element.getBoundingClientRect().top)
          window.scrollTo(1, element.offsetTop + 200)

        }
      
      }
      
      maxTry--;
      if (maxTry <= 0) {
        clearInterval(interval);
      }
    }, 500); */
}

export const removeHtml = (html) => {
  if (!html) return html
  if (typeof document !== 'undefined') {
    let tmp = document.createElement('DIV')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
  }
  return html
}

export const toTwoDigit = (number) => {
  if (number < 9) return '0' + number
  else return number
}
